import type { Rng } from './rng';

/**
 * Sampling layer (PLAN.md §2c). The sampler turns historical data + a seeded `Rng` into
 * N `ReturnInput`s; the deterministic core then runs once per `ReturnInput`. This file holds
 * the interface and an IID bootstrap skeleton; block bootstrap, historical full sequences,
 * and sequence-of-returns stress arrive in M3.
 */

/**
 * One simulated path's returns: a per-year decimal return (e.g. 0.07 = +7%). The simplest
 * `ReturnInput` form (a scalar series applied to the whole portfolio).
 */
export type ReturnSequence = readonly number[];

// ---------------------------------------------------------------------------
// ReturnInput — the deterministic core's return contract (M2 ↔ M3 boundary)
// ---------------------------------------------------------------------------

/**
 * `ReturnInput` is what `simulatePath(plan, returnInput)` consumes — the SINGLE return
 * contract that both M2 (hand-built / fixed assumptions) and M3 (the Sampler) target. A
 * future Sampler MUST be able to produce one of these forms; nothing here implies randomness
 * (the core stays seed-free; the sampler owns the Rng).
 *
 * Three forms, in order of richness:
 *  1. `fixed`    — one decimal rate for every year and every asset class (the simplest
 *                  "returns are X% every year" convenience used by deterministic tests).
 *  2. `sequence` — a per-year decimal return applied to the WHOLE portfolio (asset-class
 *                  agnostic). A bare `number[]` is accepted everywhere a `sequence` is and is
 *                  normalised to this form (back-compat with M0 tests).
 *  3. `matrix`   — a per-year, per-asset-class map. `assetClasses` lists the column order;
 *                  `byYear[y][a]` is the decimal return of asset class `assetClasses[a]` in
 *                  simulated year `y`. This is the form M3's correlated multi-asset bootstrap
 *                  produces (resample a whole historical year's cross-section at once).
 *
 * How account allocations map onto returns:
 *  - For `fixed`/`sequence`, every account earns the same per-year rate regardless of
 *    `allocation` (allocation is carried but not differentiated — single-pool semantics).
 *  - For `matrix`, an account's per-year return is the allocation-weighted average of the
 *    asset-class returns: `r = Σ_a (w_a / Σw) · byYear[y][a]`, where `w_a` is the account's
 *    `allocation[assetClasses[a]]` (missing keys → weight 0). Glide paths resolve to the
 *    effective allocation for that year before weighting.
 */
export type ReturnInput = FixedReturn | SequenceReturn | MatrixReturn;

/**
 * ENRICHMENT: realized-inflation carrier. Every `ReturnInput` form may OPTIONALLY carry a per-year
 * realized-inflation series (decimal), aligned 1:1 with the simulated years. It feeds CPI-indexed
 * streams (`{kind:'cpi'}` growth — `plan/model`) in `simulatePath`. OPTIONAL with a back-compat
 * default of "no inflation": when absent (every M0–M5 caller), `fixed`/`sequence` returns behave
 * EXACTLY as before and a `cpi` stream falls back to flat (cpi=0). The matrix samplers thread
 * `ReturnData.inflation` into this field via `matrixFromDraws`.
 */
export interface RealizedInflationCarrier {
  /** Per-year realized inflation (decimal), `inflation[y]` for simulated year `y`. Length should
   *  cover the horizon; the simulator treats a missing/short entry as 0 (flat). */
  readonly inflation?: readonly number[];
}

/** A single decimal rate for every year and asset class. */
export interface FixedReturn extends RealizedInflationCarrier {
  readonly kind: 'fixed';
  /** Decimal return, e.g. 0.05 = +5% every year. */
  readonly rate: number;
}

/** A per-year decimal return applied to the whole portfolio. */
export interface SequenceReturn extends RealizedInflationCarrier {
  readonly kind: 'sequence';
  /** `byYear[y]` is the decimal return in simulated year `y`. Length must cover the horizon. */
  readonly byYear: readonly number[];
}

/** A per-year, per-asset-class return map. */
export interface MatrixReturn extends RealizedInflationCarrier {
  readonly kind: 'matrix';
  /** Column order; an account's allocation keys are looked up against these. */
  readonly assetClasses: readonly string[];
  /**
   * `byYear[y][a]` = decimal return of `assetClasses[a]` in simulated year `y`.
   * Outer length must cover the horizon; each inner row length === `assetClasses.length`.
   */
  readonly byYear: ReadonlyArray<readonly number[]>;
}

/** A bare per-year array (M0 form) OR any structured `ReturnInput`. */
export type ReturnInputLike = ReturnSequence | ReturnInput;

/** Type guard: a structured `ReturnInput` (has a `kind` discriminant). */
export function isReturnInput(value: ReturnInputLike): value is ReturnInput {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'kind' in value;
}

/** Convenience constructor: a fixed rate every year. */
export function fixedReturn(rate: number): FixedReturn {
  return { kind: 'fixed', rate };
}

/** Convenience constructor: a per-year scalar series (from a bare array). */
export function sequenceReturn(byYear: readonly number[]): SequenceReturn {
  return { kind: 'sequence', byYear };
}

/**
 * Historical return data the samplers draw from — the canonical M1→engine BRIDGE shape.
 *
 * BACK-COMPAT (M2): the original M2 skeleton carried a single flat `annual: number[]` that the
 * `IidBootstrapSampler` resamples. That field is RETAINED so M2 tests stay green; M3 ADDS the
 * multi-asset cross-section below WITHOUT removing it.
 *
 * M3 MULTI-ASSET MODEL. A Thai investor's return universe is a small set of asset classes
 * (`assetClasses`, e.g. `['th_set_tr','th_gov_bond_tr','global_acwi_tr_thb','th_cash_tr']`)
 * observed over a sequence of historical periods (`periods`, e.g. `['1976',…,'2024']` or
 * `'YYYY-MM'`). `rows[t][a]` is the DECIMAL return (0.07 = +7%) of `assetClasses[a]` in
 * historical period `periods[t]` — i.e. ONE HISTORICAL YEAR'S CROSS-SECTION is the row
 * `rows[t]`. Inflation and FX travel as their own optional aligned columns so a sampler can
 * (M5) deflate to real terms or re-express a sleeve, but for M3 the correlated bootstrap
 * simply draws WHOLE ROWS: taking `rows[t]` jointly takes that year's stock+bond+global+cash
 * (+inflation +FX) together, preserving cross-asset correlation FOR FREE (PLAN.md §2c). The
 * matrix samplers emit a `MatrixReturn` whose `assetClasses` === this `assetClasses` and whose
 * `byYear[y]` is a drawn `rows[t]`; `simulatePath` then allocation-weights it per account.
 *
 * ALIGNMENT INVARIANT (validated by the bridge / samplers, throwing on violation):
 *  - every `rows[t].length === assetClasses.length`;
 *  - `periods.length === rows.length`;
 *  - if present, `inflation.length === rows.length` and `fx.length === rows.length`;
 *  - `assetClasses` are distinct; `rows.length >= 1`.
 * Rows are CONTEMPORANEOUS: index `t` of every column refers to the same historical period, so
 * a single draw of index `t` keeps all series aligned (this is what preserves correlation).
 */
export interface ReturnData {
  /**
   * @deprecated M2 scalar pool. Annual decimal returns, one per historical year, applied to
   * the whole portfolio. Retained for the `IidBootstrapSampler` and back-compat; M3 prefers
   * the multi-asset `assetClasses`/`rows` cross-section. A bridge MAY populate `annual` from a
   * single representative sleeve (e.g. a 60/40 blend) so legacy callers keep working.
   */
  readonly annual: readonly number[];
  /**
   * Column order of the multi-asset matrix: asset-class / series ids that line up with account
   * `allocation` keys (e.g. `th_set_tr`, `global_acwi_tr_thb`, `cash`). Distinct. Optional only
   * for back-compat with a pure-`annual` M2 fixture; the M3 matrix samplers REQUIRE it.
   */
  readonly assetClasses?: readonly string[];
  /** Aligned historical period labels (`YYYY` or `YYYY-MM`), one per `rows` entry. */
  readonly periods?: readonly string[];
  /**
   * The historical cross-section matrix. `rows[t][a]` = decimal return of `assetClasses[a]` in
   * period `periods[t]`. A correlated multi-asset bootstrap draws WHOLE rows (`rows[t]`).
   */
  readonly rows?: ReadonlyArray<readonly number[]>;
  /** Optional aligned inflation column (decimal), `inflation[t]` for period `periods[t]`. */
  readonly inflation?: readonly number[];
  /** Optional aligned FX-return column (decimal, THB-per-USD return), `fx[t]` for `periods[t]`. */
  readonly fx?: readonly number[];
}

export interface Sampler {
  /**
   * Produce `nPaths` `ReturnInput`s of length >= `horizon`, drawing randomness from `rng`.
   * Pure w.r.t. its inputs: same `data` + same seeded `rng` -> identical outputs. This is the
   * M2↔M3 boundary: each produced `ReturnInput` is exactly what `simulatePath` consumes.
   */
  sample(data: ReturnData, horizon: number, nPaths: number, rng: Rng): ReturnInput[];
}

/**
 * IID bootstrap: each simulated year independently resamples one historical annual return
 * (sampling with replacement). Destroys autocorrelation by design — block bootstrap (M3)
 * preserves it. Deterministic for a fixed seed because all draws come from `rng`. Emits the
 * scalar `sequence` form of `ReturnInput`; multi-asset matrix sampling is M3.
 */
export class IidBootstrapSampler implements Sampler {
  sample(data: ReturnData, horizon: number, nPaths: number, rng: Rng): ReturnInput[] {
    const pool = data.annual;
    if (pool.length === 0) {
      throw new Error('IidBootstrapSampler: empty return data');
    }
    const paths: ReturnInput[] = [];
    for (let p = 0; p < nPaths; p++) {
      const seq = new Array<number>(horizon);
      for (let y = 0; y < horizon; y++) {
        const idx = rng.nextInt(pool.length);
        // pool is non-empty and idx in [0, pool.length) -> always defined.
        seq[y] = pool[idx] as number;
      }
      paths.push(sequenceReturn(seq));
    }
    return paths;
  }
}
